import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { fetchContentBlocks, getContentValue } from '../lib/content';

const defaultNavLinks = ['Home', 'Products', 'Contact', 'Blog'];

const socialLinks = [
  {
    label: 'X',
    href: 'https://x.com/encreagetech',
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@encreagetech',
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@encreagetech',
  },
];

function resolveNavPath(label) {
  const normalizedLabel = label.toLowerCase().trim();

  if (normalizedLabel === 'home') {
    return '/';
  }

  if (normalizedLabel === 'work' || normalizedLabel === 'selected work') {
    return '/work';
  }

  if (normalizedLabel === 'store' || normalizedLabel === 'encreage store') {
    return '/store';
  }

  return `/${normalizedLabel.replace(/\s+/g, '-')}`;
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current stroke-[1.8]">
      <path d="M6 6 18 18" />
      <path d="M18 6 6 18" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
      <path d="M18.901 2H21.98l-6.727 7.686L23.14 22h-6.175l-4.834-6.357L6.57 22H3.49l7.196-8.224L1.14 2h6.332l4.37 5.767L18.901 2Zm-1.08 18.162h1.706L6.544 3.742H4.713L17.82 20.162Z" />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
      <path d="M23.5 6.2a3 3 0 0 0-2.1-2.12C19.47 3.5 12 3.5 12 3.5s-7.47 0-9.4.58A3 3 0 0 0 .5 6.2 31.6 31.6 0 0 0 0 12a31.6 31.6 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.12c1.93.58 9.4.58 9.4.58s7.47 0 9.4-.58a3 3 0 0 0 2.1-2.12A31.6 31.6 0 0 0 24 12a31.6 31.6 0 0 0-.5-5.8ZM9.6 15.85V8.15L16.4 12l-6.8 3.85Z" />
    </svg>
  );
}

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
      <path d="M16.77 2h-3.14v12.62a2.54 2.54 0 1 1-2.54-2.54c.21 0 .42.03.62.08V8.95a5.64 5.64 0 1 0 5.06 5.61V8.39a8.05 8.05 0 0 0 4.72 1.53V6.78a4.94 4.94 0 0 1-4.72-4.78Z" />
    </svg>
  );
}

function SocialIcon({ label }) {
  if (label === 'X') {
    return <XIcon />;
  }

  if (label === 'YouTube') {
    return <YouTubeIcon />;
  }

  return <TikTokIcon />;
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const location = useLocation();
  const MotionButton = motion.button;
  const MotionAside = motion.aside;
  const MotionLink = motion.a;

  const { data: contentBlocks = [] } = useQuery({
    queryKey: ['siteContent'],
    queryFn: fetchContentBlocks,
  });

  const navValue = getContentValue(contentBlocks, 'nav', '');
  const navLinks =
    typeof navValue === 'string' && navValue.trim()
      ? navValue
          .split(',')
          .map((link) => link.trim())
          .filter((link) => link && !['store', 'encreage store'].includes(link.toLowerCase()))
      : defaultNavLinks;

  useEffect(() => {
    setMenuOpen(false);
    setConnectOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!connectOpen) {
      document.body.style.overflow = '';
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setConnectOpen(false);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [connectOpen]);

  const navItems = navLinks.map((item) => {
    const to = resolveNavPath(item);
    const isActive = to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

    return { item, to, isActive };
  });

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[color:var(--site-header)] backdrop-blur-xl">
        <nav className="page-width flex h-[88px] items-center justify-between px-5 md:px-8">
          <Link to="/" className="font-display text-[2rem] tracking-tight text-[color:var(--site-header-ink)]">
            Encreage<span className="text-[#a9bcff]">.</span>
          </Link>

          <ul className="hidden items-center gap-10 md:flex">
            {navItems.map(({ item, to, isActive }) => (
              <li key={item}>
                <Link
                  to={to}
                  className={`relative pb-1 font-mono text-[10px] uppercase tracking-[0.3em] transition-colors ${
                    isActive ? 'text-[color:var(--site-header-ink)]' : 'text-[color:var(--site-header-muted)] hover:text-[color:var(--site-header-ink)]'
                  }`}
                >
                  {item}
                  <span
                    className={`absolute inset-x-0 -bottom-1 h-px bg-[#8aa4ff] transition-opacity ${
                      isActive ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="header-button hidden md:inline-flex"
              onClick={() => setConnectOpen(true)}
              aria-expanded={connectOpen}
              aria-controls="connect-panel"
            >
              Connect
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md border border-white/10 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-[color:var(--site-header-ink)] md:hidden"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-label="Toggle navigation"
            >
              {menuOpen ? 'Close' : 'Menu'}
            </button>
          </div>
        </nav>

        {menuOpen && (
          <div className="border-t border-white/5 md:hidden">
            <div className="page-width px-5 py-4">
              <div className="rounded-[24px] border border-white/10 bg-white/5 px-4 py-5">
                <div className="flex flex-col gap-4">
                  {navItems.map(({ item, to, isActive }) => (
                    <Link
                      key={item}
                      to={to}
                      className={`text-[11px] uppercase tracking-[0.28em] ${
                        isActive ? 'text-white' : 'text-[color:var(--site-header-muted)]'
                      }`}
                    >
                      {item}
                    </Link>
                  ))}
                  <button
                    type="button"
                    className="header-button mt-2 w-full"
                    onClick={() => {
                      setMenuOpen(false);
                      setConnectOpen(true);
                    }}
                  >
                    Connect
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      <AnimatePresence>
        {connectOpen && (
          <>
            <MotionButton
              type="button"
              className="fixed inset-0 z-[60] bg-slate-950/45 backdrop-blur-[2px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setConnectOpen(false)}
              aria-label="Close social sidebar overlay"
            />

            <MotionAside
              id="connect-panel"
              className="fixed right-0 top-0 z-[70] flex h-screen w-full max-w-sm flex-col border-l border-slate-200 bg-[rgba(251,249,244,0.96)] px-6 pb-8 pt-6 shadow-[-24px_0_80px_rgba(15,23,42,0.18)] backdrop-blur-xl"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="eyebrow normal-case">connect</p>
                  <h2 className="mt-3 font-display text-5xl leading-none tracking-tight text-slate-900">
                    find us here:
                  </h2>
                </div>
                <button
                  type="button"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-900"
                  onClick={() => setConnectOpen(false)}
                  aria-label="Close social sidebar"
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="mt-10 flex-1 space-y-4">
                {socialLinks.map((socialLink) => (
                  <MotionLink
                    key={socialLink.label}
                    href={socialLink.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex items-center justify-between rounded-[24px] border border-slate-200 bg-white/88 px-5 py-4 transition-colors hover:border-slate-300"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 16 }}
                    transition={{ duration: 0.22 }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white">
                        <SocialIcon label={socialLink.label} />
                      </div>
                      <p className="text-lg text-slate-900">{socialLink.label}</p>
                    </div>
                  </MotionLink>
                ))}
              </div>
            </MotionAside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
